// src/pages/pharmacy/ManageInventory.js
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Image as ImageIcon, AlertCircle } from 'lucide-react';
import apiService from '../../services/api';

const ManageInventory = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productLimit, setProductLimit] = useState(3);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    genericName: '',
    description: '',
    category: 'Pain Relief',
    price: '',
    stock: '',
    prescriptionRequired: false,
    manufacturer: '',
    dosage: '',
    form: 'Tablet'
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiService.pharmacies.getProducts();
      
      if (response.success) {
        setProducts(response.data || []);
      }

      // Fetch stats to get product limit
      const statsResponse = await apiService.pharmacies.getStats();
      if (statsResponse.success) {
        setProductLimit(statsResponse.data?.productLimit || 3);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (2MB as per backend)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image size should be less than 2MB');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPEG, JPG, PNG, GIF, and WebP images are allowed');
        return;
      }

      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Prepare product data
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock)
      };

      let productId;

      if (editingProduct) {
        
        const response = await apiService.pharmacies.updateProduct(
          editingProduct._id,
          productData
        );
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to update product');
        }

        productId = editingProduct._id;
       
      } else {
        // Create new product
       
        const response = await apiService.pharmacies.createProduct(productData);
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to create product');
        }

        productId = response.data?._id;
       
      }

      
      if (selectedImage && productId) {
       
        const uploadSuccess = await uploadProductImage(productId, selectedImage);
        
        if (uploadSuccess) {
          console.log('✅ Image uploaded successfully');
        } else {
          console.warn('⚠️ Image upload failed, but product was saved');
          alert('Product saved, but image upload failed. Please try uploading the image again.');
        }
      }

      alert(editingProduct ? 'Product updated successfully!' : 'Product added successfully!');
      setShowModal(false);
      resetForm();
      await fetchProducts(); // Refresh the product list
      
    } catch (error) {
      console.error('❌ Error saving product:', error);
      alert(error.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const uploadProductImage = async (productId, file) => {
    try {
      setUploading(true);
      
      const response = await apiService.pharmacies.uploadProductImage(productId, file);

      return response.success;
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      genericName: product.genericName || '',
      description: product.description || '',
      category: product.category || 'Pain Relief',
      price: product.price || '',
      stock: product.stock || '',
      prescriptionRequired: product.prescriptionRequired || false,
      manufacturer: product.manufacturer || '',
      dosage: product.dosage || '',
      form: product.form || 'Tablet'
    });
    setImagePreview(product.image || null);
    setSelectedImage(null); // Reset selected image
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.pharmacies.deleteProduct(productId);
      
      if (response.success) {
        alert('Product deleted successfully');
        await fetchProducts();
      } else {
        throw new Error(response.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert(error.message || 'Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      genericName: '',
      description: '',
      category: 'Pain Relief',
      price: '',
      stock: '',
      prescriptionRequired: false,
      manufacturer: '',
      dosage: '',
      form: 'Tablet'
    });
    setEditingProduct(null);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const canAddProduct = products.length < productLimit;

  if (loading && products.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manage Inventory</h1>
          <p className="text-gray-600 mt-1">
            Products: {products.length}/{productLimit}
          </p>
        </div>
        <button
          onClick={() => {
            if (canAddProduct) {
              resetForm();
              setShowModal(true);
            } else {
              alert(`You have reached the product limit (${productLimit}) for your current plan`);
            }
          }}
          disabled={!canAddProduct}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            canAddProduct
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {!canAddProduct && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900">Product Limit Reached</h3>
              <p className="text-yellow-700 text-sm mt-1">
                You have reached the maximum of {productLimit} products on your current plan.
                Upgrade to add more products.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <div key={product._id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="h-48 bg-gray-200 relative">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="flex items-center justify-center h-full"
                style={{ display: product.image ? 'none' : 'flex' }}
              >
                <ImageIcon className="w-16 h-16 text-gray-400" />
              </div>
              {product.prescriptionRequired && (
                <span className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                  Rx Required
                </span>
              )}
              {product.stock === 0 && (
                <span className="absolute bottom-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                  Out of Stock
                </span>
              )}
              {product.stock > 0 && product.stock < 10 && (
                <span className="absolute bottom-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">
                  Low Stock
                </span>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
              {product.genericName && (
                <p className="text-sm text-gray-500 mb-2">{product.genericName}</p>
              )}
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                {product.description}
              </p>
              <div className="flex justify-between items-center mb-3">
                <span className="text-lg font-bold text-blue-600">
                  KES {product.price?.toLocaleString() || '0'}
                </span>
                <span className={`text-sm ${
                  product.stock === 0 ? 'text-red-600 font-semibold' :
                  product.stock < 10 ? 'text-orange-600 font-semibold' :
                  'text-gray-500'
                }`}>
                  Stock: {product.stock}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(product)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(product._id)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Yet</h3>
          <p className="text-gray-600 mb-4">Start adding products to your inventory</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Your First Product
          </button>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Image {!editingProduct && '(1 image allowed)'}
                </label>
                <div className="space-y-2">
                  {imagePreview && (
                    <div className="flex items-center gap-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setSelectedImage(null);
                        }}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleImageChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500">
                    Supported formats: JPEG, PNG, GIF, WebP. Max size: 2MB
                  </p>
                </div>
              </div>

              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Paracetamol"
                />
              </div>

              {/* Generic Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Generic Name
                </label>
                <input
                  type="text"
                  name="genericName"
                  value={formData.genericName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Acetaminophen"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe the medicine, its uses, and benefits"
                />
              </div>

              {/* Category and Form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Pain Relief">Pain Relief</option>
                    <option value="Antibiotics">Antibiotics</option>
                    <option value="Vitamins & Supplements">Vitamins & Supplements</option>
                    <option value="Cold & Flu">Cold & Flu</option>
                    <option value="Digestive Health">Digestive Health</option>
                    <option value="Heart & Blood Pressure">Heart & Blood Pressure</option>
                    <option value="Diabetes">Diabetes</option>
                    <option value="Allergy">Allergy</option>
                    <option value="Skin Care">Skin Care</option>
                    <option value="First Aid">First Aid</option>
                    <option value="Prescription">Prescription</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form *
                  </label>
                  <select
                    name="form"
                    value={formData.form}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Tablet">Tablet</option>
                    <option value="Capsule">Capsule</option>
                    <option value="Syrup">Syrup</option>
                    <option value="Injection">Injection</option>
                    <option value="Cream">Cream</option>
                    <option value="Ointment">Ointment</option>
                    <option value="Drops">Drops</option>
                    <option value="Inhaler">Inhaler</option>
                    <option value="Spray">Spray</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Price and Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (KES) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    required
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Manufacturer and Dosage */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., GlaxoSmithKline"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dosage
                  </label>
                  <input
                    type="text"
                    name="dosage"
                    value={formData.dosage}
                    onChange={handleInputChange}
                    placeholder="e.g., 500mg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Prescription Required */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="prescriptionRequired"
                  name="prescriptionRequired"
                  checked={formData.prescriptionRequired}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="prescriptionRequired" className="text-sm font-medium text-gray-700">
                  Prescription Required
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={loading || uploading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || uploading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading || uploading 
                    ? (uploading ? 'Uploading Image...' : 'Saving...') 
                    : (editingProduct ? 'Update Product' : 'Add Product')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageInventory;